import { useContext, useEffect, useState } from 'react'

import { faCheck, faServer, faUser } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@scoir/avatar'
import { SelectableIconChips } from '@scoir/college-search'
import { ConfirmationDialog } from '@scoir/modals'
import { Pill } from '@scoir/pills'

import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'
import EasySetup from './EasySetup'
import ManualSetup from './ManualSetup'

const useStyles = makeStyles((theme) => ({
  configureBox: {
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
}))

const ConfigureIntegration = () => {
  const classes = useStyles()
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const { state } = useContext(IntegrationConfigurationContext)
  const [open, setOpen] = useState(false)
  const [canEdit, setCanEdit] = useState(state.Slate == null && state.SFTP == null)
  const activated = state.HandraiserEnabled || state.isDocsDeliveryEnabled

  const onConfirmEdit = () => {
    setOpen(false)
    setCanEdit(true)
  }

  useEffect(() => {
    // Reset selection if configuration is set
    setSelectedOption(null)
    setCanEdit(false)
  }, [state.ConfiguredDeliveryMethod])

  const statusPill = () => {
    if (state.ConfiguredDeliveryMethod && !selectedOption) {
      return canEdit ? (
        <Pill color="warning">Editing Connection</Pill>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Edit Connection
        </Button>
      )
    }
    switch (selectedOption) {
      case 'easy_setup':
        return <Pill color="info">Easy Slate Setup In Progress</Pill>
      case 'manual_setup':
        return <Pill color="info">Manual Setup In Progress</Pill>
      default:
        return <Pill>Not Started</Pill>
    }
  }

  const getUI = () => {
    switch (selectedOption) {
      case 'easy_setup':
        return (
          <EasySetup
            backToOptions={() => {
              setSelectedOption(null)
              setCanEdit(state.Slate == null && state.SFTP == null)
            }}
          />
        )
      case 'manual_setup':
        return (
          <ManualSetup
            backToOptions={() => {
              setSelectedOption(null)
              setCanEdit(state.Slate == null && state.SFTP == null)
            }}
          />
        )
      default:
        return (
          <SelectableIconChips
            gridItemProps={{ xs: 6 }}
            onSelect={(value) => setSelectedOption(value)}
            options={[
              {
                title: 'Easy Slate Setup',
                subtitle: 'Quickly connect using your Slate user details.',
                faIcon: faUser,
                value: 'easy_setup',
                selected: state.Slate?.Status === 'Live',
              },
              {
                title: 'Manual Setup',
                subtitle: 'Connect via SFTP for full control over your configuration.',
                faIcon: faServer,
                value: 'manual_setup',
                selected: state.SFTP?.Status === 'Live',
              },
            ]}
          />
        )
    }
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
      >
        <Typography variant="h6">Configure Integration</Typography>
        {statusPill()}
      </Box>
      {!canEdit && state.ConfiguredDeliveryMethod ? (
        <Box
          display="flex"
          alignItems="center"
          gridGap={8}
          marginTop={2}
        >
          <Avatar
            iconColor="success"
            variant="circular"
            size="sm"
            fallbackIcon={<FontAwesomeIcon icon={faCheck} />}
          />
          {`${state.ConfiguredDeliveryMethod === 'slate' ? 'Easy Slate Setup' : 'Manual Setup'} complete and ${
            activated ? 'activated' : 'ready to activate'
          }.`}
        </Box>
      ) : (
        <Box className={classes.configureBox}>{getUI()}</Box>
      )}
      <ConfirmationDialog
        open={open}
        headerText="Edit Configuration?"
        onConfirm={onConfirmEdit}
        onClose={() => setOpen(false)}
      >
        <Typography>
          Are you sure you want to edit configuration? This affects both Document Delivery and Handraisers configuration
          setups. Only Easy Slate Setup or Manual can be selected.
        </Typography>
      </ConfirmationDialog>
    </>
  )
}

export default ConfigureIntegration
